// The raise: one sheet. Frame v2.12 template L584-609, script L930.
//
//   How urgent?                 the title, over "Is your line stopped right
//                               now?" and three buttons in a row — Line is
//                               stopped ★★★ · Will stop soon ★★ · Needs
//                               attention ★
//   What's wrong?               the tiles, two columns, each with its "→ who"
//                               line, once a severity is picked
//   the questions               one at a time, under a rule, each with Skip
//                               and Don't know as well as its chips
//   A few words · optional      words, dictation, photos
//   Send · <route>              red once the two picks are made, grey and
//                               saying what is missing until then
//
// ONE SHEET, NOT THREE SCREENS. The merge ran this as a screen of its own —
// severity, then the tiles, then a confirm card, each replacing the frame —
// because the Andon app did. The frame keeps it in a sheet over whatever tab
// the + was tapped from: the operator can see the whole shape of the raise
// at once, a mis-tap on the severity row is corrected by tapping the right
// button rather than pressing Back through a guard, and the tab bar is
// still there under the scrim when it closes. It is still the `raise`
// screen state and still `/raise` in the address bar, so a reload mid-raise
// comes back to an open sheet rather than to home.
//
// WHAT THE SHEET KEEPS FROM THE CARD IT REPLACED. The server-driven question
// set — round 0's constants from home, the ranked half fetched the moment
// the constants are answered, replacing chips in place, bounded by a wait
// so a hung request cannot strand anybody; the asked register on screen;
// the say-it block; the station seal before the send; and the landing on
// the thread. What it drops: the who-gets-told block (the route is on the
// Send button, as the frame has it) and the leave guard (there is no Back
// to guard).
//
// Props rather than queries for what it draws, so the kitchen sink can show
// it on a stub; `RaiseSheetLive` below wires the real ones.

import { useEffect, useMemo, useRef, useState } from 'react';
import type { AlertTypeKey, AndonRules, OperatorHomeVM, PreRaiseAnswer, PreRaiseQuestion, RoutingPreviewVM } from '@/api/andon/types';
import { andonApi } from '@/api/andon/client';
import { Question } from '@/components/andon/Question';
import { SayItBlock } from '@/components/andon/SayItBlock';
import { Button } from '@/components/ui/Button';
import { Icon, PodMark } from '@/components/ui/Icon';
import { Sheet } from '@/components/ui/Sheet';
import { useT } from '@/i18n/useT';
import type { Translations } from '@/i18n/strings';
import { preRaiseFor, stopInQuestion } from '@/hooks/usePreRaise';
import { useRaise } from '@/hooks/useRaise';
import { answerTo, askOrder, canSend, MAX_QUESTIONS, nextUnanswered, readyToRank, remaining } from '@/lib/askFlow';
import { gridTap, type StationRef } from '@/lib/raiseFlow';
import { raiseGrid, SAFETY_LINK_TILE, SEVERITY_COPY, SEVERITY_ORDER, STAR_COLOR, starLabel, tileFor } from '@/lib/severity';
import { useAndonHome, useRoutingPreview } from '@/queries/andon';
import { useStore } from '@/store';
import type { DraftAttachment } from '@/store/slices/andon';
import { openSafetyIncidentReport } from '../RaiseSheet';

/**
 * HOW LONG THE SEND MAY BE HELD, and it is the reason holding it is safe.
 *
 * The gate in `canSend` waits for the ranked question because that question
 * is usually the objective of the tile. What it must never do is wait for
 * ever: a request that HANGS rather than fails leaves `ranked` null, and
 * without this an operator at a stopped machine would be behind a button
 * that never lights and no explanation on screen. `.catch` does not cover
 * it — a hung request never rejects.
 *
 * Eight seconds. Long enough that the ordinary Odoo round trip and even a
 * model call land inside it and the question gets asked; short enough that
 * the worst case is a pause somebody notices rather than a cord that will
 * not pull. On expiry the sheet behaves as if the ranked half came back
 * empty — send live, question asked on the alert a few seconds later.
 */
const RANKED_WAIT_MS = 8_000;

/** The raise, as the sheet needs it: a pending flag and a send. */
export interface RaiseMutation {
  isPending: boolean;
  mutate: (input: { type: AlertTypeKey; answers: PreRaiseAnswer[]; attachments: DraftAttachment[]; sealed: StationRef | null | undefined }) => void;
}

export function AndonRaiseSheet({
  open,
  onClose,
  home,
  homePending,
  preview,
  ask,
  raise,
  rules,
  onPickStation,
  onSignOut,
}: {
  open: boolean;
  onClose: () => void;
  /** Round 0's constants and the session's station. Undefined while loading or failed. */
  home: OperatorHomeVM | undefined;
  homePending: boolean;
  /** Who the current tile reaches, for the Send button's route. */
  preview: RoutingPreviewVM | undefined;
  /** The ranked half, for this pod and these answers. */
  ask: (input: { type: AlertTypeKey; productionId: number | null; answers: PreRaiseAnswer[] }) => Promise<PreRaiseQuestion[]>;
  raise: RaiseMutation;
  /** The acknowledge targets, for the severity row's hint. Null until the server has said. */
  rules: AndonRules | null;
  /** The device has no station: the one thing that fixes it. */
  onPickStation: () => void;
  /** The session has no employee: the one thing that fixes it. */
  onSignOut: () => void;
}) {
  const t = useT();
  const stars = useStore((s) => s.draftStars);
  const setDraftStars = useStore((s) => s.setDraftStars);
  const type = useStore((s) => s.draftType);
  const setDraftType = useStore((s) => s.setDraftType);
  const podId = useStore((s) => s.draftPodId);
  const podName = useStore((s) => s.draftPodName);
  const addDraftAnswer = useStore((s) => s.addDraftAnswer);
  const dropDraftAnswers = useStore((s) => s.dropDraftAnswers);
  const attachments = useStore((s) => s.draftAttachments);
  const workcentreId = useStore((s) => s.workcentreId);
  const wcName = useStore((s) => s.workcentreName);
  const flName = useStore((s) => s.flowlineName);

  /**
   * Whether a severity has been TAPPED on this sheet. The draft holds a
   * severity from the moment it is reset, because the store cannot hold
   * "none"; the frame's `r.stars` can, and the tiles do not appear until it
   * is picked. Local, and reset when the sheet opens.
   */
  const [sevPicked, setSevPicked] = useState(false);
  useEffect(() => {
    if (open) setSevPicked(false);
  }, [open]);

  // ── the questions ──────────────────────────────────────────────────────
  // Held here as well as in the store: the last chip and the send can happen
  // in the same tick, and reading the store back would send the answers as
  // they were one question ago.
  const [answers, setAnswers] = useState<PreRaiseAnswer[]>([]);
  const [skipped, setSkipped] = useState<string[]>([]);
  /**
   * The ranked half. Everything constant came down with home and is already
   * in the browser; these cannot — a defect list ordered by what happens at
   * this station on this pod type needs the pod, and the pod is not known
   * until identity is answered. `null` is "not back yet"; `[]` is "came back
   * with nothing", and the two are opposite states for the send.
   */
  const [ranked, setRanked] = useState<PreRaiseQuestion[] | null>(null);
  /** The constant answers the ranked half on screen was fetched FOR — see `startRanked`. */
  const rankedFor = useRef<string | null>(null);
  /** The request we stopped waiting for — a late answer must not take the send back. */
  const gaveUpOn = useRef<string | null>(null);

  const constants = useMemo(() => preRaiseFor(home, type, podId != null), [home, type, podId]);
  // "When will this stop the line?" — never on the Test tile, which asks nothing standard.
  const stopIn = type === 'test' ? null : stopInQuestion(home);

  const signatureOf = (given: PreRaiseAnswer[]): string => constants.map((c) => `${c.question}=${answerTo(c.question, given) ?? ''}`).join('|');

  const startRanked = (given: PreRaiseAnswer[]) => {
    if (!type) return;
    const signature = signatureOf(given);
    if (rankedFor.current === signature) return;
    rankedFor.current = signature;
    setRanked(null);
    // The bound. A timer from a superseded request finds `rankedFor` moved
    // on and does nothing — the same guard the response path uses.
    setTimeout(() => {
      if (rankedFor.current !== signature) return;
      gaveUpOn.current = signature;
      setRanked((r) => r ?? []);
    }, RANKED_WAIT_MS);
    void ask({ type, productionId: podId, answers: given })
      // A third answer may have landed while this was in flight; the slower
      // request must not win.
      .then((qs) => {
        if (rankedFor.current === signature && gaveUpOn.current !== signature) setRanked(qs);
      })
      // One question fewer is a worse raise, not a failed one. The alert
      // asks it properly a few seconds later.
      .catch(() => {
        if (rankedFor.current === signature) setRanked([]);
      });
  };

  // Nothing constant left to ask — a scan already attached the pod, or this
  // tile has no constants — so the ranked request goes out at once. Waits
  // for home: before it lands `constants` is empty for every tile.
  useEffect(() => {
    if (home && type && constants.length === 0) startRanked([]);
    // Once per tile, once home is here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, home == null]);

  /** A new tile means a new set of questions; nothing from the last one survives. */
  const pickType = (next: AlertTypeKey) => {
    if (next === type) return;
    dropDraftAnswers(answers.map((a) => a.question));
    setAnswers([]);
    setSkipped([]);
    setRanked(null);
    rankedFor.current = null;
    gaveUpOn.current = null;
    setDraftType(next);
  };

  const queue = useMemo(() => askOrder({ stars, stopIn, constants, ranked }), [stars, stopIn, constants, ranked]);
  const asking = type ? nextUnanswered(queue, answers, skipped) : null;
  const left = type ? remaining(queue, answers, skipped) : 0;
  const waiting = type != null && ranked == null;
  const narrowing = waiting && rankedFor.current != null;

  const onAnswer = (question: string, answer: string) => {
    let given = [...answers.filter((a) => a.question !== question), { question, answer }];
    // A changed constant invalidates what was asked under it: the stale
    // ranked answers must not ride to Odoo as facts about a question this
    // raise no longer puts.
    if (readyToRank(constants, given) && signatureOf(given) !== rankedFor.current) {
      const stale = (ranked ?? []).map((q) => q.question);
      if (stale.length > 0) {
        given = given.filter((a) => !stale.includes(a.question));
        dropDraftAnswers(stale);
      }
    }
    setAnswers(given);
    setSkipped((s) => s.filter((q) => q !== question));
    addDraftAnswer(question, answer);
    if (readyToRank(constants, given)) startRanked(given);
  };

  /**
   * Skip: settled for the gate, recorded nowhere. See `settled` in lib/askFlow.
   *
   * ONCE EVERY CONSTANT IS SETTLED THE RANKED HALF IS ASKED FOR ANYWAY, with
   * whatever answers there are. `readyToRank` wants them all answered, and a
   * skipped constant never will be — so without this the request never
   * fired, `ranked` stayed null, and the send waited for a question that
   * was never coming. The server ranks with what it has or refuses, and
   * either way the sheet moves on.
   */
  const onSkip = (question: string) => {
    const next = skipped.includes(question) ? skipped : [...skipped, question];
    setSkipped(next);
    if (constants.every((c) => answerTo(c.question, answers) != null || next.includes(c.question))) startRanked(answers);
  };

  /** Re-open a settled question by dropping what settled it. */
  const unsettle = (question: string) => {
    setAnswers((prev) => prev.filter((a) => a.question !== question));
    setSkipped((prev) => prev.filter((q) => q !== question));
  };

  // ── the send ───────────────────────────────────────────────────────────
  const tile = type ? tileFor(type) : undefined;
  const route = preview?.routes || (tile ? t[tile.routes] : '');
  const ready = sevPicked && type != null && canSend(queue, answers, waiting, skipped) && !raise.isPending;
  const sealed: StationRef | null | undefined = home ? (home.station ? { workcentreId: home.station.workcentreId, flowlineId: home.station.flowlineId } : null) : undefined;

  const sendText = raise.isPending
    ? t.raisingT
    : !sevPicked
      ? t.raisePickHowUrgentT
      : !type
        ? t.raisePickWhatsWrongT
        : left > 0
          ? left === 1
            ? t.raiseOneMoreAnswerT
            : `${left} ${t.raiseMoreAnswersT}`
          : waiting
            ? t.raiseWaitingLastQuestionT
            : `${t.raiseSendToT} ${route}`;

  const kicker = [t.andonTabT, wcName, podName ?? flName].filter(Boolean).join(' · ');
  const grid = raiseGrid(home?.tiles);

  // An andon has to say where it came from, and the server refuses to raise
  // one without a station; caught here rather than at the send. No employee
  // behind the session is the other thing that stops a raise before it
  // starts — once home has ANSWERED, because a sheet that accuses the
  // account and withdraws it a second later is worse than a moment of
  // nothing. A home that failed outright says nothing about the employee,
  // so the flow goes on and the raise reports what the server thinks.
  const blocked =
    workcentreId == null
      ? ('station' as const)
      : homePending
        ? ('checking' as const)
        : home && home.operator == null
          ? ('operator' as const)
          : null;

  return (
    <Sheet open={open} onClose={onClose} title={t.raiseHowUrgentT} hideTitle>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', font: "600 12px 'Space Grotesk',sans-serif", color: 'var(--sub)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{kicker}</span>
            <span style={{ display: 'block', font: "700 19px 'Space Grotesk',sans-serif", marginTop: 2, lineHeight: 1.25, color: 'var(--ink)' }}>{t.raiseHowUrgentT}</span>
          </span>
        </div>

        {blocked === 'station' ? (
          <Notice title={t.raiseNeedStationTitleT} lead={t.raiseNeedStationLeadT}>
            <Button variant="primary" size="lg" onClick={onPickStation} icon={<Icon name="grid" size={18} />}>
              {t.changeStationT}
            </Button>
          </Notice>
        ) : blocked === 'checking' ? (
          <div style={{ display: 'grid', placeItems: 'center', minHeight: 160, textAlign: 'center' }}>
            <div>
              <PodMark size={38} style={{ color: 'var(--accent)', animation: 'ipPulse 1.6s infinite' }} />
              <div style={{ marginTop: 14, font: '500 12.5px Manrope,sans-serif', color: 'var(--sub)' }}>{t.raiseCheckingT}</div>
            </div>
          </div>
        ) : blocked === 'operator' ? (
          <Notice title={t.raiseCannotTitleT} lead={t.raiseCannotLeadT} foot={t.raiseCannotFootT}>
            <Button variant="primary" size="lg" onClick={onSignOut} icon={<Icon name="logout" size={16} />}>
              {t.signOutDevice}
            </Button>
          </Notice>
        ) : (
          <>
            {/* ── how urgent ──────────────────────────────────────────── */}
            <div>
              <div style={{ font: '700 12.5px Manrope,sans-serif', color: 'var(--ink)', marginBottom: 8 }}>{t.raiseLineStoppedQT}</div>
              <div style={{ display: 'flex', gap: 7 }}>
                {SEVERITY_ORDER.map((sev) => {
                  const on = sevPicked && stars === sev;
                  const minutes = rules?.ackMinutes[String(sev) as '1' | '2' | '3'];
                  return (
                    <button
                      key={sev}
                      type="button"
                      className="press-soft"
                      aria-pressed={on}
                      onClick={() => {
                        setDraftStars(sev);
                        setSevPicked(true);
                      }}
                      // The frame's `sevBtn`: a picked three-star wears the danger
                      // tint, any other pick is ink, the rest sit quiet.
                      style={{
                        flex: 1,
                        minHeight: 46,
                        borderRadius: 11,
                        padding: '6px 4px',
                        background: on ? (sev === 3 ? 'rgba(255,90,95,.12)' : 'var(--ink)') : 'var(--surface2)',
                        color: on ? (sev === 3 ? STAR_COLOR[3] : 'var(--bg)') : 'var(--ink)',
                        border: `1.5px solid ${on ? (sev === 3 ? STAR_COLOR[3] : 'var(--ink)') : 'transparent'}`,
                        font: '700 12px Manrope,sans-serif',
                        whiteSpace: 'normal',
                        lineHeight: 1.2,
                      }}
                      title={minutes != null ? `${t.raiseResponseT} ${minutes} ${t.minT}` : undefined}
                    >
                      {t[SEVERITY_COPY[sev].label]} {starLabel(sev)}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── what's wrong ────────────────────────────────────────── */}
            {sevPicked && (
              <div>
                <div style={{ font: '700 12.5px Manrope,sans-serif', color: 'var(--ink)', marginBottom: 8 }}>{t.raiseWhatsWrongT}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {grid.map((item) => {
                    const cell = item.kind === 'alert' ? item.tile : SAFETY_LINK_TILE;
                    const tap = gridTap(item);
                    const on = tap.kind === 'pick' && tap.type === type;
                    return (
                      <button
                        key={item.kind === 'alert' ? item.tile.key : 'safety-link'}
                        type="button"
                        className="press-hard"
                        aria-pressed={on}
                        onClick={() => {
                          if (tap.kind === 'pick') return pickType(tap.type);
                          // The Safety tile raises nothing: the WHS report, in a new tab.
                          onClose();
                          openSafetyIncidentReport();
                        }}
                        style={{
                          minHeight: 78,
                          borderRadius: 15,
                          border: `1.5px solid ${on ? 'var(--ink)' : 'var(--line)'}`,
                          background: on ? 'var(--surface2)' : 'var(--surface)',
                          padding: 12,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          gap: 6,
                          textAlign: 'left',
                          whiteSpace: 'normal',
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 7, font: '700 13.5px Manrope,sans-serif', color: 'var(--ink)', lineHeight: 1.25 }}>
                          <Icon name={cell.icon} size={15} strokeWidth={2} style={{ color: cell.color, flex: 'none' }} />
                          {t[cell.label]}
                        </span>
                        <span style={{ font: '500 10.5px Manrope,sans-serif', color: 'var(--sub)', lineHeight: 1.35 }}>→ {t[cell.routes]}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── the questions ───────────────────────────────────────── */}
            {sevPicked && type && queue.length > 0 && (
              <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12 }}>
                <Progress queue={queue.length} answered={queue.length - left} t={t} />
                {/* The asked register, on screen: every question settled so
                    far, with its answer or "Skipped", tappable to re-open. */}
                {queue
                  .filter((q) => answerTo(q.question, answers) != null || skipped.includes(q.question))
                  .map((q, i, all) => {
                    const answer = answerTo(q.question, answers);
                    return (
                      <button
                        key={q.question}
                        type="button"
                        className="press"
                        onClick={() => unsettle(q.question)}
                        aria-label={`${t.raiseChangeAnswerT} ${q.question}`}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '0 0 11px', marginBottom: 11, borderBottom: i === all.length - 1 && asking ? '1px solid var(--line)' : 'none', textAlign: 'left' }}
                      >
                        <Icon name={answer != null ? 'check' : 'undo'} size={15} strokeWidth={3} style={{ color: answer != null ? 'var(--pos)' : 'var(--sub)' }} />
                        <span style={{ flex: 1, minWidth: 0, font: '500 12px Manrope,sans-serif', color: 'var(--sub)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shortLabel(q.question)}</span>
                        <span style={{ font: '700 12px Manrope,sans-serif', color: answer != null ? 'var(--ink)' : 'var(--sub)', flex: 'none' }}>{answer ?? t.qSkipT}</span>
                      </button>
                    );
                  })}
                {asking ? (
                  <>
                    <Question
                      // Keyed on the question so moving on genuinely resets the
                      // box, the camera and the field.
                      key={asking.question}
                      question={asking.question}
                      options={asking.options}
                      scan={asking.scan ?? false}
                      searchIn={asking.kind === 'search' ? (asking.searchIn ?? null) : null}
                      minutes={asking.kind === 'minutes'}
                      searchOptions={asking.searchOptions ?? []}
                      freeTextHint={asking.resolve === 'identity' ? t.qHintIdentityT : asking.resolve === 'stopIn' ? t.qHintMinutesT : undefined}
                      pending={raise.isPending}
                      onAnswer={(answer) => onAnswer(asking.question, answer)}
                      onSkip={() => onSkip(asking.question)}
                    />
                    {narrowing && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 12, padding: '8px 11px', borderRadius: 10, background: 'var(--surface2)' }}>
                        <span aria-hidden="true" style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--sub)', animation: 'ipPulse 1.6s infinite' }} />
                        <span style={{ font: '500 10.5px Manrope,sans-serif', color: 'var(--sub)' }}>{t.raiseNarrowingT}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <p style={{ margin: 0, font: '500 11.5px/1.55 Manrope,sans-serif', color: 'var(--sub)' }}>{narrowing ? t.raiseOneMoreComingT : t.raiseThatIsEverythingT}</p>
                )}
              </div>
            )}

            {/* ── a few words · optional ──────────────────────────────── */}
            {sevPicked && type && (
              <div>
                <div style={{ font: '600 11px Manrope,sans-serif', color: 'var(--sub)', letterSpacing: '.06em', textTransform: 'uppercase' }}>{t.raiseFewWordsOptionalT}</div>
                <SayItBlock />
                <p style={{ margin: '12px 0 0', font: '500 11px/1.5 Manrope,sans-serif', color: 'var(--sub)', borderTop: '1px solid var(--line)', paddingTop: 10 }}>{t.raiseAttachedNoteT}</p>
              </div>
            )}

            {/* ── send ────────────────────────────────────────────────── */}
            <button
              type="button"
              className="press-soft"
              disabled={!ready}
              onClick={() => type && raise.mutate({ type, answers, attachments, sealed })}
              style={{
                width: '100%',
                minHeight: 48,
                borderRadius: 13,
                background: ready ? 'var(--danger)' : 'var(--surface)',
                border: `1px solid ${ready ? 'var(--danger)' : 'var(--line)'}`,
                color: ready ? '#fff' : 'var(--sub)',
                font: "700 13.5px 'Space Grotesk',sans-serif",
                opacity: ready ? 1 : 0.6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              {ready && <Icon name="hazard" size={18} strokeWidth={2} />}
              {sendText}
            </button>
          </>
        )}
      </div>
    </Sheet>
  );
}

/** The sheet on the real session: home, the routing preview, the ask and the raise. */
export function RaiseSheetLive({ open, onClose }: { open: boolean; onClose: () => void }) {
  const type = useStore((s) => s.draftType);
  const rules = useStore((s) => s.andonRules);
  const workcentreId = useStore((s) => s.workcentreId);
  const openOverlay = useStore((s) => s.openOverlay);
  const home = useAndonHome(open);
  const preview = useRoutingPreview(open ? type : null, workcentreId);
  const raise = useRaise();
  return (
    <AndonRaiseSheet
      open={open}
      onClose={onClose}
      home={home.data}
      homePending={home.isPending}
      preview={preview.data}
      ask={andonApi.ask}
      raise={raise}
      rules={rules}
      onPickStation={() => openOverlay('station')}
      onSignOut={() => openOverlay('settings')}
    />
  );
}

/**
 * One card, for the two things that stop a raise before it starts.
 *
 * Both are about this session rather than this andon, and both have one
 * action that fixes them. Answering alerts and everything else keep working
 * in either case, which the foot says.
 */
function Notice({ title, lead, foot, children }: { title: string; lead: string; foot?: string; children: React.ReactNode }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 18, padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <span aria-hidden="true" style={{ width: 44, height: 44, borderRadius: 13, background: 'var(--dangerSoft)', color: 'var(--danger)', display: 'grid', placeItems: 'center' }}>
        <Icon name="hazard" size={22} strokeWidth={2} />
      </span>
      <div style={{ font: "700 18px 'Space Grotesk',sans-serif" }}>{title}</div>
      <div style={{ font: '500 13px/1.5 Manrope,sans-serif', color: 'var(--sub)' }}>{lead}</div>
      <div style={{ marginTop: 6 }}>{children}</div>
      {foot && <div style={{ font: '500 11.5px/1.6 Manrope,sans-serif', color: 'var(--sub)' }}>{foot}</div>}
    </div>
  );
}

/**
 * A dot per question, and the count beside them.
 *
 * The queue CAN lengthen once — the ranked half lands a second after the
 * tile is tapped and adds the question that scopes it. So it says "2 of 3"
 * in words as well as dots: a row of dots quietly gaining one is a progress
 * bar going backwards, where "2 of 3" becoming "2 of 4" is a fact the
 * operator can read. `MAX_QUESTIONS` is the ceiling and it is the server's
 * number — see `lib/askFlow.ts`.
 */
function Progress({ queue, answered, t }: { queue: number; answered: number; t: Translations }) {
  const dots = Math.max(queue, 1);
  const done = answered >= queue;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
      {Array.from({ length: Math.min(dots, MAX_QUESTIONS) }, (_, i) => (
        <span key={i} aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: i < answered ? 'var(--pos)' : i === answered ? 'var(--sub)' : 'var(--line)' }} />
      ))}
      <span style={{ flex: 1 }} />
      <span style={{ font: '600 10px Manrope,sans-serif', color: done ? 'var(--pos)' : 'var(--sub)' }}>
        {answered} {t.routeOfDone} {queue} {t.raiseAnsweredT}
      </span>
    </div>
  );
}

/**
 * A question, shortened to what it was ABOUT, for the settled rows. The full
 * wording is what was asked and what is recorded; this is only the label.
 */
function shortLabel(question: string): string {
  const trimmed = question.trim().replace(/\?$/, '');
  return trimmed.length > 28 ? `${trimmed.slice(0, 27).trimEnd()}…` : trimmed;
}
